import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { soccerPath, soccerUrl } from '../utils/paths';
import { CLUB_SPORTS, getClubSportLabel } from '../config/clubSports';
import './AdminPanel.css';

const AdminPanel = () => {
  const { isAdmin, isAuthenticated, user } = useAuth();
  const [activeTab, setActiveTab] = useState('teams');
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [pendingPlayers, setPendingPlayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddPlayerForm, setShowAddPlayerForm] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [playerFormData, setPlayerFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    position: 'MIDFIELDER',
    skillLevel: 5,
    skillCategory: '',
  });
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: [],
  });
  const [addingPlayerToClub, setAddingPlayerToClub] = useState(null); // Player ID being added to club
  const [selectedClubId, setSelectedClubId] = useState('');
  const [showAddClubForm, setShowAddClubForm] = useState(false);
  const [editingClubId, setEditingClubId] = useState(null);
  const [clubFormData, setClubFormData] = useState({
    name: '',
    description: '',
    sport: 'SOCCER',
    logoUrl: '',
    imageUrl: '',
  });
  const [selectedClubAdminUserId, setSelectedClubAdminUserId] = useState('');
  const [assignAdminSelections, setAssignAdminSelections] = useState({}); // clubId -> selected userId for "Assign club admin"

  const loadTeams = async () => {
    try {
      setLoading(true);
      const data = await api.getAllTeams(user?.id);
      setTeams(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };


  const loadPlayers = async () => {
    try {
      setLoading(true);
      const data = await api.getAllPlayersAdmin();
      setPlayers(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingPlayers = async () => {
    try {
      setLoading(true);
      const data = await api.getPendingPlayers();
      setPendingPlayers(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load pending players');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const data = await api.getAllUsersAdmin();
      console.log('Loaded users data:', data);
      // Normalize the data to ensure isApprovedCreator field exists
      const normalizedData = data.map(user => ({
        ...user,
        // Handle both possible field names from backend (isApprovedCreator or approvedCreator)
        isApprovedCreator: user.isApprovedCreator !== undefined 
          ? user.isApprovedCreator 
          : (user.approvedCreator !== undefined ? user.approvedCreator : false)
      }));
      console.log('Normalized users data:', normalizedData);
      setUsers(normalizedData);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !isAdmin()) {
      return;
    }
    if (activeTab === 'teams') {
      loadTeams();
    } else if (activeTab === 'players') {
      loadPlayers();
      loadClubs(); // Load clubs so they're available for "Add to Club" feature
    } else if (activeTab === 'users') {
      loadUsers(true);
    } else if (activeTab === 'clubs') {
      loadClubs();
      loadUsers(false); // Load users for club admin selection and assign-admin dropdown
    } else if (activeTab === 'player-approvals') {
      loadPendingPlayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAuthenticated]);

  if (!isAuthenticated || !isAdmin()) {
    return <Navigate to="/login" replace />;
  }

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) {
      return;
    }

    try {
      await api.deleteTeam(teamId);
      setSuccess('Team deleted successfully');
      loadTeams();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete team');
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm('Are you sure you want to delete this player?')) {
      return;
    }

    try {
      await api.deletePlayerAdmin(playerId);
      setSuccess('Player deleted successfully');
      loadPlayers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete player');
    }
  };

  const handleApprovePlayer = async (playerId) => {
    try {
      await api.approvePlayerAdmin(playerId);
      setSuccess('Player approved successfully');
      loadPendingPlayers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to approve player');
    }
  };

  const handleRejectPlayer = async (playerId) => {
    if (!window.confirm('Are you sure you want to reject this player?')) {
      return;
    }

    try {
      await api.rejectPlayerAdmin(playerId);
      setSuccess('Player rejected');
      loadPendingPlayers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reject player');
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.addPlayerAdmin({
        ...playerFormData,
        dateOfBirth: playerFormData.dateOfBirth || null,
        skillCategory: playerFormData.skillCategory || null,
      });
      setSuccess('Player added successfully');
      setPlayerFormData({
        name: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        position: 'MIDFIELDER',
        skillLevel: 5,
        skillCategory: '',
      });
      setShowAddPlayerForm(false);
      setEditingPlayerId(null);
      loadPlayers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add player');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlayer = (player) => {
    setEditingPlayerId(player.id);
    setPlayerFormData({
      name: player.name || '',
      email: player.email || '',
      phoneNumber: player.phoneNumber || '',
      dateOfBirth: player.dateOfBirth ? player.dateOfBirth.slice(0, 10) : '',
      position: player.position || 'MIDFIELDER',
      skillLevel: player.skillLevel || 5,
      skillCategory: player.skillCategory || '',
    });
    setShowAddPlayerForm(true);
  };

  const handleUpdatePlayer = async (e) => {
    e.preventDefault();
    if (!editingPlayerId) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.updatePlayerAdmin(editingPlayerId, {
        ...playerFormData,
        dateOfBirth: playerFormData.dateOfBirth || null,
        skillCategory: playerFormData.skillCategory || null,
      });
      setSuccess('Player updated successfully');
      setPlayerFormData({
        name: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        position: 'MIDFIELDER',
        skillLevel: 5,
        skillCategory: '',
      });
      setEditingPlayerId(null);
      setShowAddPlayerForm(false);
      loadPlayers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update player');
    } finally {
      setLoading(false);
    }
  };

  const cancelPlayerForm = () => {
    setPlayerFormData({
      name: '',
      email: '',
      phoneNumber: '',
      dateOfBirth: '',
      position: 'MIDFIELDER',
      skillLevel: 5,
      skillCategory: '',
    });
    setEditingPlayerId(null);
    setShowAddPlayerForm(false);
  };

  const handlePlayerFormChange = (e) => {
    const { name, value } = e.target;
    setPlayerFormData((prev) => ({
      ...prev,
      [name]: name === 'skillLevel' ? parseInt(value) : value,
    }));
  };

  const formatPosition = (position) => {
    return position.charAt(0) + position.slice(1).toLowerCase().replace('_', ' ');
  };

  const handleAssignCreatorRole = async (userId) => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      const response = await api.assignCreatorRole(userId);
      console.log('Assign creator role response:', response);
      
      // Immediately update local state for instant UI feedback
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { 
                ...user, 
                roles: response.roles || [...(user.roles || []), 'TEAM_CREATOR'],
                isApprovedCreator: false 
              } 
            : user
        )
      );
      
      setSuccess('Team creator role assigned successfully. The user now has the TEAM_CREATOR role but needs approval to create teams.');
      
      // Small delay to ensure database update is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload users list to get fresh data from server to ensure sync
      await loadUsers(false); // Don't show loading since we're already showing it
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Failed to assign creator role');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCreatorRole = async (userId) => {
    if (!window.confirm('Are you sure you want to remove the creator role from this user?')) {
      return;
    }
    try {
      setError('');
      setLoading(true);
      await api.removeCreatorRole(userId);
      setSuccess('Creator role removed successfully');
      
      // Small delay to ensure database update is complete
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Reload users list to get fresh data from server
      await loadUsers(false); // Don't show loading since we're already showing it
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Failed to remove creator role');
    } finally {
      setLoading(false);
    }
  };

  // User CRUD Operations
  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.createUserAdmin(userFormData);
      setSuccess('User created successfully');
      setUserFormData({
        name: '',
        email: '',
        password: '',
        roles: [],
      });
      setShowAddUserForm(false);
      await loadUsers(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUserId(user.id);
    setUserFormData({
      name: user.name || '',
      email: user.email || '',
      password: '', // Don't pre-fill password
      roles: user.roles ? [...user.roles] : [],
    });
    setShowAddUserForm(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUserId) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // If password is empty, don't send it
      const updateData = { ...userFormData };
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      }

      await api.updateUserAdmin(editingUserId, updateData);
      setSuccess('User updated successfully');
      setUserFormData({
        name: '',
        email: '',
        password: '',
        roles: [],
      });
      setEditingUserId(null);
      setShowAddUserForm(false);
      await loadUsers(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await api.deleteUserAdmin(userId);
      setSuccess('User deleted successfully');
      await loadUsers(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePasswordResetLink = async (userItem) => {
    setError('');
    setSuccess('');
    try {
      setLoading(true);
      const res = await api.adminGeneratePasswordResetLink(userItem.id);
      const url =
        res.resetUrl ||
        `${soccerUrl('/reset-password')}?token=${encodeURIComponent(res.token)}`;
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        /* clipboard unavailable (e.g. non-HTTPS) */
      }
      let expText = '';
      if (res.expiresAt != null) {
        const raw = res.expiresAt;
        const asDate =
          typeof raw === 'string'
            ? new Date(raw)
            : Array.isArray(raw) && raw.length >= 3
              ? new Date(raw[0], raw[1] - 1, raw[2], raw[3] ?? 0, raw[4] ?? 0, raw[5] ?? 0)
              : null;
        if (asDate && !Number.isNaN(asDate.getTime())) {
          expText = ` Expires: ${asDate.toLocaleString()}.`;
        }
      }
      setSuccess(
        copied
          ? `Reset link copied for ${userItem.email}.${expText} Single use—generate a new link if needed.`
          : `Copy this reset link for ${userItem.email}:${expText} ${url}`
      );
      setTimeout(() => setSuccess(''), copied ? 12000 : 60000);
    } catch (err) {
      setError(err.message || 'Failed to generate reset link');
    } finally {
      setLoading(false);
    }
  };

  const handleUserFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      const role = value;
      setUserFormData((prev) => ({
        ...prev,
        roles: checked
          ? [...(prev.roles || []), role]
          : (prev.roles || []).filter((r) => r !== role),
      }));
    } else {
      setUserFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const cancelUserForm = () => {
    setUserFormData({
      name: '',
      email: '',
      password: '',
      roles: [],
    });
    setEditingUserId(null);
    setShowAddUserForm(false);
  };

  const loadClubs = async () => {
    try {
      setLoading(true);
      setError('');
      // Load all clubs for admin management (includes PENDING_APPROVAL from new registrations)
      const data = await api.getAllClubsAdmin();
      const list = Array.isArray(data) ? data : [];
      // Sort so Pending Approval (new registrations) show first
      const sorted = list.slice().sort((a, b) => {
        const order = { PENDING_APPROVAL: 0, APPROVED: 1, REJECTED: 2 };
        return (order[a.status] ?? 1) - (order[b.status] ?? 1);
      });
      setClubs(sorted);
    } catch (err) {
      setError(err.message || 'Failed to load clubs');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClub = async (clubId) => {
    try {
      setLoading(true);
      setError('');
      await api.approveClub(clubId);
      setSuccess('Club approved successfully');
      loadClubs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to approve club');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClub = async (clubId) => {
    if (!window.confirm('Are you sure you want to reject this club?')) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      await api.rejectClub(clubId);
      setSuccess('Club rejected');
      loadClubs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reject club');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignClubAdmin = async (clubId, userId) => {
    if (!userId) {
      setError('Please select a user to assign as club admin');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await api.assignClubAdmin(clubId, userId);
      setSuccess('Club admin assigned successfully');
      setAssignAdminSelections((prev) => ({ ...prev, [clubId]: '' }));
      loadClubs();
      loadUsers(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to assign club admin');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClub = async (clubId) => {
    if (!window.confirm('Are you sure you want to delete this club? This action cannot be undone.')) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      await api.deleteClubAdmin(clubId);
      setSuccess('Club deleted successfully');
      loadClubs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete club');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClub = (club) => {
    setEditingClubId(club.id);
    setClubFormData({
      name: club.name || '',
      description: club.description || '',
      sport: club.sport || 'SOCCER',
      logoUrl: club.logoUrl || '',
      imageUrl: club.imageUrl || '',
    });
    setSelectedClubAdminUserId(club.adminUserId || '');
    setShowAddClubForm(true);
  };

  const handleAddClub = async (e) => {
    e.preventDefault();
    if (!selectedClubAdminUserId) {
      setError('Please select a club admin user');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.createClubAdmin(clubFormData, selectedClubAdminUserId);
      setSuccess('Club created successfully');
      setClubFormData({ name: '', description: '', sport: 'SOCCER', logoUrl: '', imageUrl: '' });
      setSelectedClubAdminUserId('');
      setShowAddClubForm(false);
      loadClubs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to create club');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClub = async (e) => {
    e.preventDefault();
    if (!editingClubId) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.updateClubAdmin(editingClubId, clubFormData);
      setSuccess('Club updated successfully');
      setClubFormData({ name: '', description: '', sport: 'SOCCER', logoUrl: '', imageUrl: '' });
      setEditingClubId(null);
      setShowAddClubForm(false);
      loadClubs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update club');
    } finally {
      setLoading(false);
    }
  };

  const cancelClubForm = () => {
    setClubFormData({ name: '', description: '', sport: 'SOCCER', logoUrl: '', imageUrl: '' });
    setSelectedClubAdminUserId('');
    setEditingClubId(null);
    setShowAddClubForm(false);
  };

  const handleClubFormChange = (e) => {
    const { name, value } = e.target;
    setClubFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (loading && teams.length === 0 && users.length === 0 && players.length === 0) {
    return <div className="loading">Loading admin panel...</div>;
  }

  return (
    <div className="admin-panel">
      <div className="card">
        <h2>Admin Panel</h2>
        
        <div className="admin-tabs">
          <button
            className={activeTab === 'teams' ? 'active' : ''}
            onClick={() => setActiveTab('teams')}
          >
            Manage Teams
          </button>
          <button
            className={activeTab === 'players' ? 'active' : ''}
            onClick={() => setActiveTab('players')}
          >
            Manage Players
          </button>
          <button
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => setActiveTab('users')}
          >
            Manage Users
          </button>
          <button
            className={activeTab === 'clubs' ? 'active' : ''}
            onClick={() => setActiveTab('clubs')}
          >
            Manage Clubs
          </button>
          <button
            className={activeTab === 'player-approvals' ? 'active' : ''}
            onClick={() => setActiveTab('player-approvals')}
          >
            Player Approvals
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {activeTab === 'teams' && (
          <div className="admin-content">
            <h3>All Teams ({teams.length})</h3>
            <button onClick={loadTeams} className="btn btn-secondary" style={{ marginBottom: '1rem' }}>
              Refresh
            </button>
            
            {teams.length === 0 ? (
              <p>No teams found</p>
            ) : (
              <div className="admin-teams-list">
                {teams.map((team) => (
                  <div key={team.id} className="admin-team-card">
                    <div className="admin-team-header">
                      <h4>{team.name}</h4>
                      <div className="admin-team-actions">
                        <Link to={soccerPath(`/teams/${team.id}`)} className="btn btn-secondary" style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}>
                          View
                        </Link>
                        <Link to={soccerPath(`/teams/${team.id}/edit`)} className="btn btn-primary" style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}>
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDeleteTeam(team.id)}
                          className="btn btn-danger"
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="admin-team-info">
                      <p><strong>Creator:</strong> {team.createdByName || team.creator?.name || 'Unknown'}</p>
                      <p><strong>Members:</strong> {team.members?.length || 0}</p>
                      <p><strong>Locked:</strong> {team.isLocked ? 'Yes' : 'No'}</p>
                      {team.isLocked && team.approvedCreatorId && (
                        <p><strong>Approved Creator ID:</strong> {team.approvedCreatorId}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'players' && (
          <div className="admin-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>All Players ({players.length})</h3>
              <button 
                onClick={() => {
                  setShowAddPlayerForm(!showAddPlayerForm);
                  if (showAddPlayerForm) {
                    cancelPlayerForm();
                  }
                }} 
                className="btn btn-primary"
              >
                {showAddPlayerForm ? 'Cancel' : 'Add Player'}
              </button>
            </div>
            
            {showAddPlayerForm && (
              <div className="card" style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
                <h4>{editingPlayerId ? 'Edit Player' : 'Add New Player'}</h4>
                <form onSubmit={editingPlayerId ? handleUpdatePlayer : handleAddPlayer}>
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={playerFormData.name}
                      onChange={handlePlayerFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={playerFormData.email}
                      onChange={handlePlayerFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={playerFormData.phoneNumber}
                      onChange={handlePlayerFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of birth (optional)</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={playerFormData.dateOfBirth}
                      onChange={handlePlayerFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Position *</label>
                    <select
                      name="position"
                      value={playerFormData.position}
                      onChange={handlePlayerFormChange}
                      required
                    >
                      <option value="GOALKEEPER">Goalkeeper</option>
                      <option value="DEFENDER">Defender</option>
                      <option value="MIDFIELDER">Midfielder</option>
                      <option value="FORWARD">Forward</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Skill Level: {playerFormData.skillLevel}/10</label>
                    <input
                      type="range"
                      name="skillLevel"
                      min="1"
                      max="10"
                      value={playerFormData.skillLevel}
                      onChange={handlePlayerFormChange}
                    />
                    <div className="skill-labels">
                      <span>1 (Beginner)</span>
                      <span>10 (Expert)</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Experience level (optional)</label>
                    <select
                      name="skillCategory"
                      value={playerFormData.skillCategory}
                      onChange={handlePlayerFormChange}
                      style={{ width: '100%', padding: '0.5rem' }}
                    >
                      <option value="">— Select —</option>
                      <option value="BEGINNER">Beginner</option>
                      <option value="INTERMEDIATE">Intermediate</option>
                      <option value="EXPERT">Expert</option>
                    </select>
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (editingPlayerId ? 'Updating...' : 'Adding...') : (editingPlayerId ? 'Update Player' : 'Add Player')}
                  </button>
                  {editingPlayerId && (
                    <button 
                      type="button" 
                      onClick={cancelPlayerForm} 
                      className="btn btn-secondary" 
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Cancel
                    </button>
                  )}
                </form>
              </div>
            )}

            <button onClick={loadPlayers} className="btn btn-secondary" style={{ marginBottom: '1rem' }}>
              Refresh
            </button>
            
            {players.length === 0 ? (
              <p>No players found</p>
            ) : (
              <div className="player-list">
                {players.map((player) => (
                  <div key={player.id} className="player-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <h3>{player.name}</h3>
                        <div className="player-info">
                          <p><strong>Email:</strong> {player.email}</p>
                          {player.phoneNumber && <p><strong>Phone:</strong> {player.phoneNumber}</p>}
                          <p><strong>Position:</strong> {formatPosition(player.position)}</p>
                          <p><strong>Skill Level:</strong> {player.skillLevel}/10</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEditPlayer(player)}
                          className="btn btn-secondary"
                          disabled={loading}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={async () => {
                            // Ensure clubs are loaded before showing the dropdown
                            if (clubs.length === 0) {
                              await loadClubs();
                            }
                            setAddingPlayerToClub(player.id);
                            setSelectedClubId('');
                          }}
                          className="btn btn-primary"
                          disabled={loading}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          Add to Club
                        </button>
                        <button
                          onClick={() => handleDeletePlayer(player.id)}
                          className="btn btn-danger"
                          disabled={loading}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          Delete
                        </button>
                      </div>
                      {addingPlayerToClub === player.id && (
                        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Select Club:
                          </label>
                          <select
                            value={selectedClubId}
                            onChange={(e) => setSelectedClubId(e.target.value)}
                            style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
                          >
                            <option value="">-- Select a club --</option>
                            {clubs.filter(c => c.status === 'APPROVED').length === 0 ? (
                              <option value="" disabled>No approved clubs available</option>
                            ) : (
                              clubs.filter(c => c.status === 'APPROVED').map(club => (
                                <option key={club.id} value={club.id}>
                                  {club.name}
                                </option>
                              ))
                            )}
                          </select>
                          {clubs.length === 0 && (
                            <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                              Loading clubs...
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={async () => {
                                if (!selectedClubId) {
                                  setError('Please select a club');
                                  return;
                                }
                                try {
                                  setLoading(true);
                                  setError('');
                                  await api.addPlayerToClubAdmin(selectedClubId, player.id);
                                  setSuccess(`Player ${player.name} added to club successfully`);
                                  setAddingPlayerToClub(null);
                                  setSelectedClubId('');
                                  setTimeout(() => setSuccess(''), 3000);
                                } catch (err) {
                                  setError(err.message || 'Failed to add player to club');
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              className="btn btn-primary"
                              disabled={loading || !selectedClubId}
                              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                            >
                              Add
                            </button>
                            <button
                              onClick={() => {
                                setAddingPlayerToClub(null);
                                setSelectedClubId('');
                              }}
                              className="btn btn-secondary"
                              disabled={loading}
                              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>All Users ({users.length})</h3>
              <button 
                onClick={() => {
                  setShowAddUserForm(!showAddUserForm);
                  if (showAddUserForm) {
                    cancelUserForm();
                  }
                }} 
                className="btn btn-primary"
              >
                {showAddUserForm ? 'Cancel' : 'Add User'}
              </button>
            </div>
            
            {showAddUserForm && (
              <div className="card" style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
                <h4>{editingUserId ? 'Edit User' : 'Add New User'}</h4>
                <form onSubmit={editingUserId ? handleUpdateUser : handleAddUser}>
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={userFormData.name}
                      onChange={handleUserFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={userFormData.email}
                      onChange={handleUserFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Password {editingUserId ? '(leave blank to keep current)' : '*'}</label>
                    <input
                      type="password"
                      name="password"
                      value={userFormData.password}
                      onChange={handleUserFormChange}
                      required={!editingUserId}
                      minLength={6}
                    />
                  </div>
                  <div className="form-group">
                    <label>Roles *</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          value="PLAYER"
                          checked={userFormData.roles.includes('PLAYER')}
                          onChange={handleUserFormChange}
                          style={{ marginRight: '0.5rem' }}
                        />
                        Player
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          value="TEAM_CREATOR"
                          checked={userFormData.roles.includes('TEAM_CREATOR')}
                          onChange={handleUserFormChange}
                          style={{ marginRight: '0.5rem' }}
                        />
                        Team Creator
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          value="CLUB_CREATOR"
                          checked={userFormData.roles.includes('CLUB_CREATOR')}
                          onChange={handleUserFormChange}
                          style={{ marginRight: '0.5rem' }}
                        />
                        Club Creator
                      </label>
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (editingUserId ? 'Updating...' : 'Creating...') : (editingUserId ? 'Update User' : 'Create User')}
                  </button>
                </form>
              </div>
            )}
            
            <button onClick={loadUsers} className="btn btn-secondary" style={{ marginBottom: '1rem' }}>
              Refresh
            </button>
            
            {users.length === 0 ? (
              <p>No users found</p>
            ) : (
              <div className="admin-creators-list">
                {users.map((userItem) => {
                  const hasCreatorRole = userItem.roles && userItem.roles.includes('TEAM_CREATOR');
                  const hasAdminRole = userItem.roles && userItem.roles.includes('ADMIN');
                  const hasClubCreatorRole = userItem.roles && userItem.roles.includes('CLUB_CREATOR');
                  
                  return (
                    <div key={userItem.id} className="admin-creator-card">
                      <div className="admin-creator-header">
                        <div>
                          <h4>{userItem.name}</h4>
                          <p><strong>Email:</strong> {userItem.email}</p>
                          <p><strong>Roles:</strong> {userItem.roles ? userItem.roles.join(', ') : 'None'}</p>
                          <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                            <strong>Club admin of:</strong>{' '}
                            {(userItem.adminOfClubs && userItem.adminOfClubs.length > 0)
                              ? userItem.adminOfClubs.map((club) => (
                                  <span key={club.id}>
                                    <Link to={soccerPath(`/clubs/${club.id}`)} style={{ marginRight: '0.5rem' }}>
                                      {club.name}
                                    </Link>
                                    {userItem.adminOfClubs.indexOf(club) < userItem.adminOfClubs.length - 1 ? ', ' : ''}
                                  </span>
                                ))
                              : '—'}
                          </p>
                          {(userItem.clubMemberships && userItem.clubMemberships.length > 0) && (
                            <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                              <strong>Member of:</strong>
                              <span style={{ display: 'block', marginTop: '0.25rem', paddingLeft: '0.5rem' }}>
                                {userItem.clubMemberships.map((m) => {
                                  const roleLabel = m.roles && m.roles.length > 0 ? m.roles.join(', ') : '';
                                  const parts = [m.status];
                                  if (roleLabel) parts.push(roleLabel);
                                  if (m.clubAdmin) parts.push('Club admin');
                                  const detail = parts.join(' · ');
                                  return (
                                    <span key={m.clubId} style={{ display: 'block', marginBottom: '0.2rem' }}>
                                      <Link to={soccerPath(`/clubs/${m.clubId}`)}>{m.clubName}</Link>
                                      {' — '}
                                      <span style={{ color: m.status === 'APPROVED' ? '#155724' : m.status === 'PENDING' ? '#856404' : '#721c24' }}>
                                        {detail}
                                      </span>
                                    </span>
                                  );
                                })}
                              </span>
                            </p>
                          )}
                        </div>
                        <div>
                          {hasAdminRole && (
                            <span className="badge badge-success" style={{ marginBottom: '0.5rem', display: 'block' }}>
                              Admin
                            </span>
                          )}
                          {hasCreatorRole && (
                            <span className="badge badge-success" style={{ marginBottom: '0.5rem', display: 'block' }}>
                              Team Creator
                            </span>
                          )}
                          {hasClubCreatorRole && (
                            <span className="badge badge-success" style={{ marginBottom: '0.5rem', display: 'block' }}>
                              Club Creator
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="admin-creator-actions">
                        {!hasAdminRole && (
                          <>
                            {!hasCreatorRole ? (
                              <button
                                onClick={() => handleAssignCreatorRole(userItem.id)}
                                className="btn btn-primary"
                                disabled={loading}
                              >
                                {loading ? 'Assigning...' : 'Assign Creator Role'}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleRemoveCreatorRole(userItem.id)}
                                className="btn btn-danger"
                                disabled={loading}
                              >
                                Remove Creator Role
                              </button>
                            )}
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => handleGeneratePasswordResetLink(userItem)}
                          className="btn btn-secondary"
                          disabled={loading}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                          title="Creates a one-time link for this user to set a new password"
                        >
                          Reset password link
                        </button>
                        <button
                          onClick={() => handleEditUser(userItem)}
                          className="btn btn-secondary"
                          disabled={loading}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteUser(userItem.id)}
                          className="btn btn-danger"
                          disabled={loading}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'clubs' && (
          <div className="admin-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>All Clubs ({clubs.length})</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  onClick={() => {
                    setShowAddClubForm(!showAddClubForm);
                    if (showAddClubForm) {
                      cancelClubForm();
                    }
                  }} 
                  className="btn btn-primary"
                >
                  {showAddClubForm ? 'Cancel' : 'Add Club'}
                </button>
                <button onClick={loadClubs} className="btn btn-secondary">
                  Refresh
                </button>
              </div>
            </div>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
              New club registrations appear here with status <strong>Pending Approval</strong>. Click <strong>Refresh</strong> to load the latest.
            </p>

            {showAddClubForm && (
              <div className="card" style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
                <h4>{editingClubId ? 'Edit Club' : 'Add New Club'}</h4>
                <form onSubmit={editingClubId ? handleUpdateClub : handleAddClub}>
                  <div className="form-group">
                    <label>Club Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={clubFormData.name}
                      onChange={handleClubFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Sport *</label>
                    <select
                      name="sport"
                      value={clubFormData.sport || 'SOCCER'}
                      onChange={handleClubFormChange}
                      required
                      style={{ width: '100%', padding: '0.5rem' }}
                    >
                      {CLUB_SPORTS.map((sport) => (
                        <option key={sport.id} value={sport.id}>
                          {sport.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={clubFormData.description}
                      onChange={handleClubFormChange}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Logo URL</label>
                    <input
                      type="url"
                      name="logoUrl"
                      value={clubFormData.logoUrl}
                      onChange={handleClubFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Image URL</label>
                    <input
                      type="url"
                      name="imageUrl"
                      value={clubFormData.imageUrl}
                      onChange={handleClubFormChange}
                    />
                  </div>
                  {!editingClubId && (
                    <div className="form-group">
                      <label>Club Admin User *</label>
                      <select
                        value={selectedClubAdminUserId}
                        onChange={(e) => setSelectedClubAdminUserId(e.target.value)}
                        required={!editingClubId}
                        style={{ width: '100%', padding: '0.5rem' }}
                      >
                        <option value="">-- Select a user --</option>
                        {users.filter(u => u.roles?.includes('CLUB_CREATOR') || u.roles?.includes('ADMIN')).map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name} ({u.email})
                          </option>
                        ))}
                      </select>
                      <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                        Select a user with CLUB_CREATOR or ADMIN role to be the club admin
                      </p>
                    </div>
                  )}
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (editingClubId ? 'Updating...' : 'Creating...') : (editingClubId ? 'Update Club' : 'Create Club')}
                  </button>
                  {editingClubId && (
                    <button 
                      type="button" 
                      onClick={cancelClubForm} 
                      className="btn btn-secondary" 
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Cancel
                    </button>
                  )}
                </form>
              </div>
            )}
            
            {clubs.length === 0 ? (
              <p>No clubs found.</p>
            ) : (
              <div className="admin-creators-list">
                {clubs.map((club) => (
                  <div key={club.id} className="admin-creator-card">
                    <div className="admin-creator-header">
                      <div>
                        <h4>{club.name}</h4>
                        {club.description && <p><strong>Description:</strong> {club.description}</p>}
                        <p><strong>Sport:</strong> {getClubSportLabel(club.sport)}</p>
                        <p><strong>Club admin:</strong> {club.adminUserId ? (users.find(u => u.id === club.adminUserId)?.name || club.adminUserId) : '—'}</p>
                        <p><strong>Status:</strong> {club.status}</p>
                        <p style={{ marginTop: '0.5rem' }}>
                          <Link to={soccerPath(`/clubs/${club.id}/admin`)} className="btn btn-secondary" style={{ padding: '0.35rem 0.75rem', fontSize: '0.9rem' }}>
                            View players & ratings
                          </Link>
                        </p>
                        <div style={{ marginTop: '0.75rem' }}>
                          <strong>Assign club admin:</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                            <select
                              value={assignAdminSelections[club.id] || ''}
                              onChange={(e) => setAssignAdminSelections((prev) => ({ ...prev, [club.id]: e.target.value }))}
                              style={{ padding: '0.35rem 0.5rem', minWidth: '180px' }}
                            >
                              <option value="">— Select user —</option>
                              {users.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.name} ({u.email})
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => handleAssignClubAdmin(club.id, assignAdminSelections[club.id])}
                              className="btn btn-primary"
                              disabled={loading || !assignAdminSelections[club.id]}
                              style={{ padding: '0.35rem 0.75rem', fontSize: '0.9rem' }}
                            >
                              Assign
                            </button>
                          </div>
                        </div>
                        {club.logoUrl && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <img src={club.logoUrl} alt={club.name} style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '4px' }} />
                          </div>
                        )}
                      </div>
                      <div>
                        <span className={`badge ${
                          club.status === 'APPROVED' ? 'badge-success' : 
                          club.status === 'PENDING_APPROVAL' ? 'badge-warning' : 
                          'badge-danger'
                        }`}>
                          {club.status === 'APPROVED' ? 'Approved' : 
                           club.status === 'PENDING_APPROVAL' ? 'Pending Approval' : 
                           'Rejected'}
                        </span>
                      </div>
                    </div>
                    <div className="admin-creator-actions">
                      {club.status === 'PENDING_APPROVAL' && (
                        <>
                          <button
                            onClick={() => handleApproveClub(club.id)}
                            className="btn btn-primary"
                            disabled={loading}
                            style={{ marginRight: '0.5rem' }}
                          >
                            {loading ? 'Approving...' : 'Approve Club'}
                          </button>
                          <button
                            onClick={() => handleRejectClub(club.id)}
                            className="btn btn-danger"
                            disabled={loading}
                            style={{ marginRight: '0.5rem' }}
                          >
                            {loading ? 'Rejecting...' : 'Reject Club'}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleEditClub(club)}
                        className="btn btn-secondary"
                        disabled={loading}
                        style={{ marginRight: '0.5rem' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClub(club.id)}
                        className="btn btn-danger"
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'player-approvals' && (
          <div className="admin-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Pending Player Approvals ({pendingPlayers.length})</h3>
              <button onClick={loadPendingPlayers} className="btn btn-secondary">
                Refresh
              </button>
            </div>
            
            {pendingPlayers.length === 0 ? (
              <p>No pending player approvals.</p>
            ) : (
              <div className="admin-creators-list">
                {pendingPlayers.map((player) => (
                  <div key={player.id} className="admin-creator-card">
                    <div className="admin-creator-header">
                      <div>
                        <h4>{player.name}</h4>
                        <p><strong>Email:</strong> {player.email}</p>
                        {player.phoneNumber && <p><strong>Phone:</strong> {player.phoneNumber}</p>}
                        <p><strong>Position:</strong> {player.position}</p>
                        <p><strong>Skill Level:</strong> {player.skillLevel}/10</p>
                        <p><strong>Status:</strong> {player.status}</p>
                      </div>
                      <div>
                        <span className="badge badge-warning">Pending Approval</span>
                      </div>
                    </div>
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleApprovePlayer(player.id)}
                        className="btn btn-primary"
                        disabled={loading}
                        style={{ marginRight: '0.5rem' }}
                      >
                        {loading ? 'Approving...' : 'Approve Player'}
                      </button>
                      <button
                        onClick={() => handleRejectPlayer(player.id)}
                        className="btn btn-danger"
                        disabled={loading}
                      >
                        {loading ? 'Rejecting...' : 'Reject Player'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;

